import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { prvekZnacky } from './doprava/Prvky';

/**
 * MapLibre canvas — navigation mode:
 *   - Mapy.com raster dlaždice + pitch 50°, zoom podle rychlosti (16,3 → 15,3)
 *   - Kamera follow polohu řidiče — plynule po snímcích mezi GPS body
 *   - Bearing = heading řidiče (kamera se otáčí, šipka je vždy nahoru)
 *   - Camera padding ⇒ marker řidiče sedí v dolní 1/3 obrazovky (jako Google Nav)
 *   - Šipka = bíle obrysovaný modrý chevron s tail-trailem
 *   - Polyline trasa = silniční geometrie z Mapy.com routing
 *   - Geofence okolo aktuální zastávky — *poloměr odvozený z trasy ve 3 minutách*
 *     (ne vzdušná vzdálenost), glow když řidič uvnitř
 *
 * Props:
 *   - bistro: {lat, lng}
 *   - zastavky: [...]
 *   - aktualniIdx: index aktuální zastávky
 *   - poloha: {lat, lng, heading} aktuální GPS řidiče
 *   - routeGeometry: [[lat,lng], ...] silniční trasa od polohy k cíli (může být [])
 *   - routeDurationSec: doba jízdy v sekundách (z `useRouteGeometry`)
 *   - geofenceThresholdSec: hranice „za rohem" v sekundách (default 180 = 3 min)
 *   - geofenceActive: bool — řidič uvnitř → glow
 *   - followCamera: bool (default true) — kamera následuje polohu
 *   - tileUrl, tileAttribution: Mapy.com tile config
 *   - isDark
 */
const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '© OpenStreetMap';

function buildRasterStyle(tileUrl, attribution) {
    return {
        version: 8,
        glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
        sources: {
            base: { type: 'raster', tiles: [tileUrl], tileSize: 256, attribution },
        },
        layers: [{ id: 'base', type: 'raster', source: 'base' }],
    };
}

export default function MapaCanvas({
    bistro, zastavky = [], aktualniIdx = 0,
    poloha = null, routeGeometry = [], routeDurationSec = null,
    routeTypy = null,   // typ úseku ('auto' | 'nezpevnena' | 'pesi') — přerušovaná linka mimo silnici
    geofenceThresholdSec = 180, geofenceActive = false,
    followCamera = true,
    tileUrl, tileAttribution = '© OpenStreetMap',
    isDark = false,
    mode = 'navigation',     // 'navigation' | 'overview' — overview vypne pitch/follow/driver/geofence
    fitPadding = null,       // { top, bottom, left, right } — overrides defaultní 60/60/50/50 pro overview fitBounds
    okrajeKamery = null,     // { top, bottom } px zakryté ovládáním — poloha se centruje do zbytku
    kandidati = [],          // [{id, lat, lng, cislo}] objednávky v teple — šedé body, ťuk = přidat do trasy
    onKandidat = null,
    onRucniPosun = null,     // rozvozce posunul mapou prstem → rodič vypne followCamera
    dopravniBody = [],       // [{typ, lat, lng}] hlášení řidičů a radary (Dopravní info)
    mista = [],              // benzinky a obchody na trase [{id, druh, lat, lng, stav}] (2026-10-29)
    aktualniMisto = null,    // id zastávky, na kterou se teď jede
}) {
    const isOverview = mode === 'overview';
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const dopravaRef = useRef([]);
    const polohaMarkerRef = useRef(null);
    const polohaElRef = useRef(null);
    const [ready, setReady] = useState(false);
    // Callback v refu — markery se kvůli nové funkci z rodiče nepřekreslují
    const onKandidatRef = useRef(onKandidat);
    onKandidatRef.current = onKandidat;
    const onRucniPosunRef = useRef(onRucniPosun);
    onRucniPosunRef.current = onRucniPosun;
    // Plynulý pohyb šipky a kamery (viz snimek())
    const pohybRef = useRef(null);       // { od, cil, start, delka, surovy, prisel }
    const rafRef = useRef(null);
    const rychlostRef = useRef(null);
    const sledovatRef = useRef(false);
    const autoZoomRef = useRef(true);

    const effectiveTileUrl = tileUrl || OSM_TILE_URL;
    const effectiveAttribution = tileUrl ? tileAttribution : OSM_ATTRIBUTION;

    // -----------------------------------------------------------------
    // Init mapy (jednou)
    // -----------------------------------------------------------------
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;

        const center = bistro?.lat && bistro?.lng
            ? [bistro.lng, bistro.lat]
            : [18.211, 49.547];

        const map = new maplibregl.Map({
            container: containerRef.current,
            style: buildRasterStyle(effectiveTileUrl, effectiveAttribution),
            center,
            zoom: isOverview ? 13 : ZOOM_POMALU,   // overview = celá trasa; navigace pak zoom podle rychlosti
            pitch: isOverview ? 0 : 50,
            bearing: 0,
            attributionControl: false,
            maxPitch: isOverview ? 0 : 70,
        });

        map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');

        map.on('load', () => {
            // ---- Trasa (line) ----
            map.addSource('route', {
                type: 'geojson',
                data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [] } },
            });
            // Per-mode šířka linky: navigation = výrazná tlustá (jízda),
            // overview = vlasová (jen indikace trasy, nepřebíjí markery).
            const w = isOverview
                ? { outline: 3,  line: 1.5, highlight: 0, hiOpacity: 0 }
                : { outline: 12, line: 7,   highlight: 2, hiOpacity: 0.55 };
            // Outline (širší tmavý okraj pro kontrast)
            map.addLayer({
                id: 'route-outline',
                type: 'line',
                source: 'route',
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: { 'line-color': '#1A0A2E', 'line-width': w.outline, 'line-opacity': 0.70 },
            });
            // Hlavní výrazná fialová linie (rozvoz brand)
            map.addLayer({
                id: 'route-line',
                type: 'line',
                source: 'route',
                filter: ['==', ['get', 'typ'], 'auto'],
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: { 'line-color': '#A855F7', 'line-width': w.line, 'line-opacity': 1 },
            });
            // Nezpevněná / pěší cesta (2026-10-26): táž fialová, jen
            // přerušovaná — mezerou prosvítá tmavý okraj, nic křiklavého.
            map.addLayer({
                id: 'route-line-mimo',
                type: 'line',
                source: 'route',
                filter: ['!=', ['get', 'typ'], 'auto'],
                layout: { 'line-join': 'round', 'line-cap': 'butt' },
                paint: { 'line-color': '#A855F7', 'line-width': w.line, 'line-opacity': 1, 'line-dasharray': [1.6, 1.1] },
            });
            // Vrchní jemný highlight (světlejší fialová) pro 3D glow feel
            // — jen v navigation módu, v overview by zahušťoval vlasovou linku.
            if (!isOverview) {
                map.addLayer({
                    id: 'route-highlight',
                    type: 'line',
                    source: 'route',
                    filter: ['==', ['get', 'typ'], 'auto'],
                    layout: { 'line-join': 'round', 'line-cap': 'round' },
                    paint: { 'line-color': '#E9D5FF', 'line-width': w.highlight, 'line-opacity': w.hiOpacity },
                });
            }

            // ---- Geofence ----
            map.addSource('geofence', {
                type: 'geojson',
                data: { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [[]] } },
            });
            map.addLayer({
                id: 'geofence-fill',
                type: 'fill',
                source: 'geofence',
                paint: {
                    'fill-color': '#FF8A00',
                    'fill-opacity': 0.10,
                },
            });
            map.addLayer({
                id: 'geofence-stroke',
                type: 'line',
                source: 'geofence',
                paint: {
                    'line-color': '#FF8A00',
                    'line-width': 2,
                    'line-dasharray': [2, 2],
                    'line-opacity': 0.7,
                },
            });

            setReady(true);
        });

        mapRef.current = map;
        return () => {
            // Reset markers/poloha refs + ready, aby další tile_url cyklus
            // znovu spustil markers useEffect na nové mapě.
            markersRef.current.forEach(m => { try { m.remove(); } catch {} });
            markersRef.current = [];
            polohaMarkerRef.current = null;
            polohaElRef.current = null;
            setReady(false);
            map.remove();
            mapRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveTileUrl]);

    // -----------------------------------------------------------------
    // Markery zastávek + bistro
    // -----------------------------------------------------------------
    useEffect(() => {
        if (!ready || !mapRef.current) return;

        markersRef.current.forEach(m => m.remove());
        markersRef.current = [];

        if (bistro?.lat && bistro?.lng) {
            const el = document.createElement('div');
            el.style.cssText = `
                width: 30px; height: 30px; border-radius: 50%;
                background: linear-gradient(135deg, #0CC0DF, #00A8C2);
                border: 2px solid #fff;
                box-shadow: 0 6px 18px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.25);
                display: flex; align-items: center; justify-content: center;
                color: #fff; font-weight: 700; font-size: 15px; line-height: 1;
                pointer-events: auto; z-index: 5;
            `;
            el.textContent = '🏠';
            markersRef.current.push(
                new maplibregl.Marker({ element: el })
                    .setLngLat([bistro.lng, bistro.lat])
                    .addTo(mapRef.current)
            );
        }

        zastavky.forEach((z, i) => {
            if (z.lat == null || z.lng == null) return;

            const isAktivni = !isOverview && i === aktualniIdx;
            const isHotova = z.stav_doruceni === 'doruceno';
            const isNezastizeno = z.stav_doruceni === 'nezastizen';

            const bgGradient = isHotova
                ? 'linear-gradient(135deg, #10B981, #34D399)'
                : isNezastizeno
                    ? 'linear-gradient(135deg, #6B7280, #9CA3AF)'
                    : isAktivni
                        ? 'linear-gradient(135deg, #FF8A00, #F59E0B)'
                        : 'linear-gradient(135deg, #6C4192, #975ACD)';

            // Malé — čísla nesmí zakrývat ulice (feedback rozvozce 2026-10-22);
            // v navigation je o kousek větší jen aktuální cíl.
            const size = isOverview ? 26 : (isAktivni ? 28 : 22);
            const fontSize = isOverview ? 13 : (isAktivni ? 14 : 11);

            const el = document.createElement('div');
            el.style.cssText = `
                width: ${size}px; height: ${size}px; border-radius: 50%;
                background: ${bgGradient};
                border: 2px solid #fff;
                box-shadow: 0 3px 10px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.25);
                display: flex; align-items: center; justify-content: center;
                color: #fff; font-weight: 800; font-size: ${fontSize}px;
                line-height: 1; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                pointer-events: auto; z-index: 5;
            `;
            el.textContent = isHotova ? '✓' : isNezastizeno ? '×' : String(i + 1);

            markersRef.current.push(
                new maplibregl.Marker({ element: el })
                    .setLngLat([z.lng, z.lat])
                    .addTo(mapRef.current)
            );
        });

        // Benzinky a obchody na trase — čtverec s ikonou, ať se neplete s objednávkou.
        mista.forEach(m => {
            if (m.lat == null || m.lng == null) return;
            const hotovo = m.stav === 'hotovo';
            const ted = m.id === aktualniMisto;
            const el = document.createElement('div');
            el.setAttribute('aria-label', m.nazev || 'Zastávka');
            el.style.cssText = `
                width: ${ted ? 30 : 26}px; height: ${ted ? 30 : 26}px; border-radius: 8px;
                background: ${hotovo ? 'linear-gradient(135deg, #10B981, #34D399)' : '#3A3F45'};
                border: 2px solid ${ted ? '#FF8A00' : '#fff'};
                box-shadow: 0 3px 10px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.25);
                display: flex; align-items: center; justify-content: center;
                font-size: ${ted ? 16 : 14}px; line-height: 1; pointer-events: auto; z-index: 5;
            `;
            el.textContent = hotovo ? '✓' : m.druh === 'benzinka' ? '⛽' : '🛒';
            markersRef.current.push(
                new maplibregl.Marker({ element: el })
                    .setLngLat([m.lng, m.lat])
                    .addTo(mapRef.current)
            );
        });

        // Objednávky v teple — šedé, přerušovaný okraj, číslo z účtenky.
        // Ťuknutí ji přidá do trasy (skládání trasy).
        kandidati.forEach(k => {
            if (k.lat == null || k.lng == null) return;
            const el = document.createElement('button');
            el.type = 'button';
            el.setAttribute('aria-label', `Přidat #${k.cislo} do trasy`);
            el.style.cssText = `
                min-width: 34px; height: 24px; padding: 0 6px; border-radius: 12px;
                background: rgba(20,20,24,0.88); border: 2px dashed #F59E0B;
                box-shadow: 0 4px 12px rgba(0,0,0,0.5);
                color: #FBBF24; font-weight: 800; font-size: 12px; line-height: 1;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                cursor: pointer; z-index: 4;
            `;
            el.textContent = `+${k.cislo}`;
            el.addEventListener('click', (e) => { e.stopPropagation(); onKandidatRef.current?.(k.id); });
            markersRef.current.push(
                new maplibregl.Marker({ element: el })
                    .setLngLat([k.lng, k.lat])
                    .addTo(mapRef.current)
            );
        });
    }, [ready, bistro?.lat, bistro?.lng, zastavky, aktualniIdx, kandidati, mista, aktualniMisto]);

    // Dopravní info: hlášení řidičů a radary (2026-10-23)
    useEffect(() => {
        if (!ready || !mapRef.current) return;
        dopravaRef.current.forEach(m => m.remove());
        dopravaRef.current = dopravniBody
            .filter(b => b.lat != null && b.lng != null)
            .map(b => new maplibregl.Marker({ element: prvekZnacky(b.typ) }).setLngLat([b.lng, b.lat]).addTo(mapRef.current));
    }, [ready, dopravniBody]);

    // -----------------------------------------------------------------
    // Šipka řidiče + kamera — plynule.
    //
    // GPS chodí zhruba po sekundě. Dřív šipka na každý bod skočila a kamera
    // k němu jela vlastní 700ms animací (easeTo), takže se obraz cukal.
    // Teď se mezi dvěma body dopočítává každý snímek (rAF): šipka i kamera
    // jedou spolu, směr se otáčí kratší cestou a na místě se neotáčí vůbec
    // (heading je při stání jen šum).
    // -----------------------------------------------------------------
    useEffect(() => {
        if (!ready || !mapRef.current) return;

        if (isOverview || !poloha?.lat || !poloha?.lng) {
            polohaMarkerRef.current?.remove();
            polohaMarkerRef.current = null;
            polohaElRef.current = null;
            pohybRef.current = null;
            return;
        }

        if (!polohaMarkerRef.current) {
            const el = document.createElement('div');
            el.style.cssText = `
                width: 72px; height: 72px;
                display: flex; align-items: center; justify-content: center;
                will-change: transform;
                pointer-events: none;
            `;
            // Nav chevron bez kruhové báze — modrý gradient, silný bílý
            // obrys pro čitelnost na libovolném podkladu, drop-shadow, pulse halo.
            el.innerHTML = `
                <svg width="72" height="72" viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <linearGradient id="rozvoz-chevron-grad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%"  stop-color="#60A5FA" />
                            <stop offset="55%" stop-color="#2563EB" />
                            <stop offset="100%" stop-color="#1E3A8A" />
                        </linearGradient>
                        <filter id="rozvoz-chevron-shadow" x="-50%" y="-50%" width="200%" height="200%">
                            <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="#000" flood-opacity="0.45" />
                        </filter>
                    </defs>
                    <!-- pulsing halo -->
                    <circle cx="48" cy="48" r="38" fill="#3B82F6" opacity="0.14">
                        <animate attributeName="r" values="32;44;32" dur="2.2s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.22;0.04;0.22" dur="2.2s" repeatCount="indefinite" />
                    </circle>
                    <g id="rozvoz-arrow-rot" transform="rotate(0 48 48)">
                        <!-- samotná šipka, žádná kruhová podložka -->
                        <path d="M48 8 L78 76 L48 60 L18 76 Z"
                              fill="url(#rozvoz-chevron-grad)"
                              stroke="#fff" stroke-width="4" stroke-linejoin="round"
                              filter="url(#rozvoz-chevron-shadow)" />
                    </g>
                </svg>
            `;
            polohaElRef.current = el;
            polohaMarkerRef.current = new maplibregl.Marker({
                element: el,
                rotationAlignment: 'map',
                pitchAlignment: 'map',
            })
                .setLngLat([poloha.lng, poloha.lat])
                .addTo(mapRef.current);
        }

        const ted = performance.now();
        const minule = pohybRef.current;
        const smer = smerJizdy(poloha, minule?.surovy, minule?.cil.heading ?? 0);
        const cil = { lat: poloha.lat, lng: poloha.lng, heading: smer };

        if (!minule || haversineM(minule.cil.lat, minule.cil.lng, cil.lat, cil.lng) > SKOK_M) {
            // První bod nebo skok (tunel, ztráta signálu) — bez animace.
            pohybRef.current = { od: cil, cil, start: ted, delka: 0, surovy: poloha, prisel: ted };
        } else {
            // Navazujeme z místa, kde je šipka právě teď (ne z minulého cíle),
            // délka animace = odstup GPS bodů → šipka jede souvisle.
            pohybRef.current = {
                od: aktualniStav(minule, ted), cil, start: ted,
                delka: Math.min(1500, Math.max(300, ted - minule.prisel)),
                surovy: poloha, prisel: ted,
            };
        }
        rychlostRef.current = poloha.speed ?? null;
        spustitAnimaci();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, poloha?.lat, poloha?.lng, poloha?.heading]);

    // Jeden snímek: pozice + směr šipky, kamera (když sleduje), zoom podle rychlosti.
    const snimek = () => {
        rafRef.current = null;
        const map = mapRef.current;
        const pohyb = pohybRef.current;
        if (!map || !pohyb || !polohaMarkerRef.current) return;

        const ted = performance.now();
        const stav = aktualniStav(pohyb, ted);
        polohaMarkerRef.current.setLngLat([stav.lng, stav.lat]);
        const arrow = polohaElRef.current?.querySelector('#rozvoz-arrow-rot');
        if (arrow) arrow.setAttribute('transform', `rotate(${stav.heading} 48 48)`);

        let bezi = ted - pohyb.start < pohyb.delka;
        if (sledovatRef.current) {
            const opts = { center: [stav.lng, stav.lat], bearing: stav.heading };
            if (autoZoomRef.current) {
                const z = map.getZoom();
                const cilZoom = zoomProRychlost(rychlostRef.current);
                if (Math.abs(cilZoom - z) > 0.01) {
                    opts.zoom = z + (cilZoom - z) * 0.04;
                    bezi = true;
                }
            }
            map.jumpTo(opts);
        }
        if (bezi) rafRef.current = requestAnimationFrame(snimek);
    };
    const spustitAnimaci = () => {
        if (rafRef.current == null) rafRef.current = requestAnimationFrame(snimek);
    };
    useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); }, []);

    // -----------------------------------------------------------------
    // Kamera: padding (poloha v dolní části volné mapy), zapnutí sledování,
    // ruční posun mapy sledování vypne (tlačítko „Na mou polohu" ho vrátí).
    // -----------------------------------------------------------------
    sledovatRef.current = followCamera && !isOverview;

    useEffect(() => {
        if (!ready || !mapRef.current || isOverview) return;
        const map = mapRef.current;
        // Posun, přiblížení i otočení prsty = rozvozce si mapu prohlíží →
        // sledování stop a naskočí tlačítko „Na mou polohu" (vrátí i zoom podle rychlosti).
        const pri = (e) => { if (e.originalEvent) onRucniPosunRef.current?.(); };
        const udalosti = ['dragstart', 'zoomstart', 'rotatestart', 'pitchstart'];
        udalosti.forEach(u => map.on(u, pri));
        return () => udalosti.forEach(u => map.off(u, pri));
    }, [ready, isOverview]);

    useEffect(() => {
        if (!ready || !mapRef.current || !followCamera || isOverview) return;

        const map = mapRef.current;
        const canvas = map.getCanvas();
        const h = canvas?.clientHeight ?? 0;
        const w = canvas?.clientWidth ?? 0;

        // Marker (center coord) se vykreslí do centroidu nepaddované oblasti.
        // centroid_y = (padding.top + h - padding.bottom) / 2
        // S `okrajeKamery` (skutečná výška horní lišty a spodního listu)
        // sedí poloha ve volné mapě mezi nimi, kousek pod středem — před
        // sebou je vidět víc cesty. Bez nich původní odhad (~210 px dole).
        let padding;
        if (okrajeKamery) {
            const volno = Math.max(0, h - okrajeKamery.top - okrajeKamery.bottom);
            padding = {
                top:    Math.round(okrajeKamery.top + volno * 0.3),
                bottom: Math.round(okrajeKamery.bottom + 16),
                left:   Math.round(w * 0.05),
                right:  Math.round(w * 0.05),
            };
        } else {
            const targetFromBottom = 250;
            const paddingBottom = 20;
            padding = {
                top:    Math.max(60, h - 2 * targetFromBottom + paddingBottom),
                bottom: paddingBottom,
                left:   Math.round(w * 0.05),
                right:  Math.round(w * 0.05),
            };
        }

        // Zpátky na polohu (i po ručním posunu) plynule, pak už vede snímek().
        autoZoomRef.current = true;
        const stav = pohybRef.current ? aktualniStav(pohybRef.current, performance.now()) : null;
        map.setPadding(padding);
        map.easeTo({
            ...(stav ? { center: [stav.lng, stav.lat], bearing: stav.heading } : {}),
            duration: 400,
        });
        const id = setTimeout(spustitAnimaci, 420);
        return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, followCamera, isOverview, okrajeKamery?.top, okrajeKamery?.bottom]);

    // -----------------------------------------------------------------
    // Polyline trasa (route)
    // -----------------------------------------------------------------
    useEffect(() => {
        if (!ready || !mapRef.current) return;
        const src = mapRef.current.getSource('route');
        if (!src) return;

        // routeGeometry je z backendu jako [[lat,lng], ...] — MapLibre chce [lng,lat]
        const coords = (routeGeometry || []).map(p => [p[1], p[0]]);
        src.setData(useky(coords, routeTypy));
    }, [ready, routeGeometry, routeTypy]);

    // -----------------------------------------------------------------
    // Geofence — okolo aktuální zastávky.
    //
    // Poloměr odvozujeme z dojezdového času: snažíme se kruh vykreslit tak,
    // aby řidič „za 3 minuty" byl právě na okraji. Když máme reálnou
    // routeGeometry, vezmeme z ní fyzický bod, který odpovídá tomuto poměru
    // (linear interp podle počtu vertices). Když routing data nejsou, použijeme
    // odhad z poslední známé rychlosti řidiče (heading + GPS speed) nebo
    // pevný fallback ~800 m.
    // -----------------------------------------------------------------
    useEffect(() => {
        if (!ready || !mapRef.current) return;
        const src = mapRef.current.getSource('geofence');
        if (!src) return;

        const cil = zastavky[aktualniIdx];
        if (isOverview || !cil?.lat || !cil?.lng) {
            src.setData({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [[]] } });
            return;
        }

        const radiusM = estimateGeofenceRadius({
            routeGeometry, routeDurationSec, thresholdSec: geofenceThresholdSec,
            cil, fallbackM: 800,
        });

        const polygon = buildCircle(cil.lat, cil.lng, radiusM);
        src.setData({
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates: [polygon] },
        });

        // Stav uvnitř/vně mění barvu (active = zelený glow).
        mapRef.current.setPaintProperty(
            'geofence-fill', 'fill-color',
            geofenceActive ? '#10B981' : '#FF8A00'
        );
        mapRef.current.setPaintProperty(
            'geofence-fill', 'fill-opacity',
            geofenceActive ? 0.22 : 0.10
        );
        mapRef.current.setPaintProperty(
            'geofence-stroke', 'line-color',
            geofenceActive ? '#10B981' : '#FF8A00'
        );
    }, [ready, zastavky, aktualniIdx, routeGeometry, routeDurationSec, geofenceThresholdSec, geofenceActive]);

    // -----------------------------------------------------------------
    // Overview: fitBounds přes bistro + zastávky (auto-refresh při změně).
    // Canvas po mountu může mít 0×0 (parent `flex: 1` se layoutuje až po
    // commit fázi), proto `resize()` před fit + rAF pro počkání na layout.
    // -----------------------------------------------------------------
    useEffect(() => {
        if (!ready || !mapRef.current || !isOverview) return;

        // Bounds buduj přes UNION bodů (bistro + zastávky) A silniční geometrie,
        // jinak silnice kolem (kdy auto musí objet) padá pod overlay.
        const pts = [];
        if (bistro?.lat && bistro?.lng) pts.push([bistro.lng, bistro.lat]);
        zastavky.forEach(z => {
            if (z.lat != null && z.lng != null) pts.push([z.lng, z.lat]);
        });
        kandidati.forEach(k => {
            if (k.lat != null && k.lng != null) pts.push([k.lng, k.lat]);
        });
        mista.forEach(m => {
            if (m.lat != null && m.lng != null) pts.push([m.lng, m.lat]);
        });
        (routeGeometry || []).forEach(p => {
            if (Array.isArray(p) && p.length >= 2) pts.push([p[1], p[0]]);
        });
        if (pts.length === 0) return;

        const run = () => {
            const map = mapRef.current;
            if (!map) return;
            map.resize();
            if (pts.length === 1) {
                map.easeTo({ center: pts[0], zoom: 14, duration: 400 });
                return;
            }
            const bounds = pts.reduce(
                (b, p) => b.extend(p),
                new maplibregl.LngLatBounds(pts[0], pts[0]),
            );
            map.fitBounds(bounds, {
                padding: fitPadding || { top: 60, bottom: 60, left: 50, right: 50 },
                duration: 500,
            });
        };
        // Dva rAFs — první po commit fázi, druhý už po reálném layoutu.
        const id = requestAnimationFrame(() => requestAnimationFrame(run));
        return () => cancelAnimationFrame(id);
    }, [ready, isOverview, bistro?.lat, bistro?.lng, zastavky, kandidati, mista, routeGeometry, fitPadding]);

    return (
        <>
            {/* Mapa ve svých barvách — dřív tmavý filtr (invert jako Flotila),
                ale cesty, hlavně vedlejší a nezpevněné, v něm za jízdy zanikaly
                (feedback rozvozce 2026-10-22). */}
            <div
                ref={containerRef}
                style={{
                    position: 'absolute', inset: 0,
                    background: '#EDEAE3',
                }}
            />
            {!tileUrl && (
                <div style={{
                    // Pod horní lištou cockpitu (Zpět + Navigovat a karta cíle), ne za ní.
                    position: 'absolute', top: 'calc(134px + env(safe-area-inset-top, 0px))', left: 10, right: 10,
                    padding: '6px 10px', borderRadius: 10,
                    background: 'rgba(245,158,11,0.18)',
                    border: '1px solid rgba(245,158,11,0.5)',
                    color: '#F59E0B', fontSize: 11, fontWeight: 600,
                    textAlign: 'center',
                }}>
                    MAPY_CZ_API_KEY není nastaven — fallback OpenStreetMap.
                </div>
            )}
        </>
    );
}

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

/**
 * Postaví GeoJSON polygon aproximující kruh — `points` vrcholů okolo (lat,lng)
 * v `radiusM` metrů. Lat/lng v stupních, polygon ve formátu [lng,lat] pro MapLibre.
 */
function buildCircle(lat, lng, radiusM, points = 48) {
    const coords = [];
    const earthR = 6371000;
    const latR = (radiusM / earthR) * (180 / Math.PI);
    const lngR = (radiusM / (earthR * Math.cos((lat * Math.PI) / 180))) * (180 / Math.PI);

    for (let i = 0; i <= points; i++) {
        const theta = (i / points) * 2 * Math.PI;
        const dx = lngR * Math.cos(theta);
        const dy = latR * Math.sin(theta);
        coords.push([lng + dx, lat + dy]);
    }
    return coords;
}

/**
 * Spočítá poloměr geofence tak, aby orientačně odpovídal `thresholdSec` jízdy.
 *
 * Strategie:
 *   1. Pokud máme routeGeometry + routeDurationSec → spočítej průměrnou
 *      rychlost na trase a poloměr = avgSpeed × thresholdSec. To je realistický
 *      odhad „za 3 min ujede".
 *   2. Bez routing dat → fallback ~800 m (≈ městská rychlost 16 km/h × 180s).
 */
function estimateGeofenceRadius({ routeGeometry, routeDurationSec, thresholdSec, cil, fallbackM }) {
    if (Array.isArray(routeGeometry) && routeGeometry.length >= 2 && routeDurationSec > 0) {
        // Délka trasy v metrech (sumovaný haversine přes vrcholy).
        let lenM = 0;
        for (let i = 1; i < routeGeometry.length; i++) {
            const [la1, ln1] = routeGeometry[i - 1];
            const [la2, ln2] = routeGeometry[i];
            lenM += haversineM(la1, ln1, la2, ln2);
        }
        const avgSpeedMps = lenM / routeDurationSec;     // m/s na celé trase
        const radiusM = avgSpeedMps * thresholdSec;
        // Clamp: rozumné meze pro UI viditelnost.
        return Math.max(200, Math.min(radiusM, 5000));
    }
    return fallbackM;
}

/** Zoom navigace podle rychlosti — ve městě blíž, na silnici dál vidět dopředu. */
const ZOOM_POMALU = 16.3;
function zoomProRychlost(speed) {
    if (speed == null) return ZOOM_POMALU;
    if (speed > 16) return 15.3;   // > ~58 km/h
    if (speed > 9) return 15.8;    // > ~32 km/h
    return ZOOM_POMALU;
}

/** Větší posun mezi dvěma GPS body se nekreslí jako jízda, ale skok. */
const SKOK_M = 300;

/** Kde je šipka v čase `ted` mezi minulou a novou polohou. */
function aktualniStav(pohyb, ted) {
    const t = pohyb.delka > 0 ? Math.min(1, (ted - pohyb.start) / pohyb.delka) : 1;
    const { od, cil } = pohyb;
    const rozdil = ((cil.heading - od.heading + 540) % 360) - 180;   // kratší cestou
    return {
        lat: od.lat + (cil.lat - od.lat) * t,
        lng: od.lng + (cil.lng - od.lng) * t,
        heading: (od.heading + rozdil * t + 360) % 360,
    };
}

/**
 * Směr jízdy pro šipku i kameru. Při stání (< ~5 km/h) je heading z GPS
 * jen šum → držíme poslední. Bez headingu ho dopočítáme z posunu.
 */
function smerJizdy(poloha, predchozi, minulySmer) {
    if (poloha.speed != null && poloha.speed < 1.5) return minulySmer;
    if (poloha.heading != null && !Number.isNaN(poloha.heading)) return poloha.heading;
    if (predchozi && haversineM(predchozi.lat, predchozi.lng, poloha.lat, poloha.lng) > 8) {
        const f1 = (predchozi.lat * Math.PI) / 180, f2 = (poloha.lat * Math.PI) / 180;
        const dl = ((poloha.lng - predchozi.lng) * Math.PI) / 180;
        const y = Math.sin(dl) * Math.cos(f2);
        const x = Math.cos(f1) * Math.sin(f2) - Math.sin(f1) * Math.cos(f2) * Math.cos(dl);
        return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
    }
    return minulySmer;
}

function haversineM(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180)
        * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Trasa rozdělená na úseky podle typu cesty: navazující úseky stejného typu
 * jsou jedna linka (vrstvy route-line / route-line-mimo filtrují podle typ).
 * Bez typů celá trasa jako silnice.
 */
function useky(coords, typy) {
    if (!typy || typy.length !== coords.length - 1 || coords.length < 2) {
        return { type: 'FeatureCollection', features: [{ type: 'Feature', properties: { typ: 'auto' }, geometry: { type: 'LineString', coordinates: coords } }] };
    }
    const features = [];
    let od = 0;
    for (let i = 1; i <= typy.length; i++) {
        if (i === typy.length || typy[i] !== typy[od]) {
            features.push({ type: 'Feature', properties: { typ: typy[od] }, geometry: { type: 'LineString', coordinates: coords.slice(od, i + 1) } });
            od = i;
        }
    }
    return { type: 'FeatureCollection', features };
}
